// Plutus (AI trading coach) chat state for the assistant dock (DOCK-01), same server conversations as the Plutus page
// (/app/ai-chat, plutusChatApi.js): resumes the most recent chat on first activation, follows chats the Plutus page
// sends, and reports usage (daily cap + the reviewed-trades nudge).
import { useCallback, useEffect, useRef, useState } from 'react';
import { listConversations, getConversation, sendChatMessage, PLUTUS_CHAT_EVENT } from '../plutusChatApi';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:5000';
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

export default function usePlutusChat(active = true) {
  const [messages, setMessages] = useState([]);
  const [conversationId, setConversationId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [usage, setUsage] = useState(null);
  const convRef = useRef(null);
  convRef.current = conversationId;
  const loadedOnce = useRef(false);

  const load = useCallback((id) => {
    const pick = id ? Promise.resolve(id) : listConversations().then((cs) => (cs[0] ? cs[0].conversation_id : null));
    return pick.then((cid) => (cid ? getConversation(cid) : null)).then((c) => {
      if (!c || !Array.isArray(c.messages)) return;
      setConversationId(c.conversation_id);
      setMessages(c.messages.map((m) => ({ role: m.role, content: m.content })));
    });
  }, []);

  useEffect(() => {
    if (!active || loadedOnce.current) return;
    loadedOnce.current = true;
    load();
    fetch(`${API_BASE}/api/ai/chat/usage`, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : null)).then(setUsage).catch(() => {});
  }, [active, load]);

  // The Plutus page sent a message: follow that conversation.
  useEffect(() => {
    const onUpdate = (e) => {
      if (e.detail?.source === 'dock') return;
      loadedOnce.current = true;
      load(e.detail?.conversationId);
    };
    window.addEventListener(PLUTUS_CHAT_EVENT, onUpdate);
    return () => window.removeEventListener(PLUTUS_CHAT_EVENT, onUpdate);
  }, [load]);

  const send = useCallback((text) => {
    const content = (text || '').trim();
    if (!content || loading) return;
    setMessages((m) => [...m, { role: 'user', content }]);
    setLoading(true);
    sendChatMessage({ conversationId: convRef.current, message: content, source: 'dock' })
      .then((d) => {
        if (d.reply) {
          if (d.conversation_id) setConversationId(d.conversation_id);
          setMessages((m) => [...m, { role: 'assistant', content: d.reply }]);
          setUsage((prev) => (prev ? { ...prev, used: d.used, cap: d.cap } : prev));
        } else if (d.cap_reached) {
          setUsage((prev) => (prev ? { ...prev, used: prev.cap } : prev));
          setMessages((m) => [...m, { role: 'assistant', capReached: true, content: d.error }]);
        } else {
          setMessages((m) => [...m, { role: 'assistant', error: true, content: d.error || 'Something went wrong.' }]);
        }
      })
      .finally(() => setLoading(false));
  }, [loading]);

  const newChat = useCallback(() => { setConversationId(null); setMessages([]); }, []);
  const showNudge = !!usage && typeof usage.reviewed_trade_count === 'number' && !usage.coach_unlocked;

  return { messages, conversationId, loading, usage, showNudge, send, newChat };
}
